import { FC, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useSort } from '@table-library/react-table-library/sort';
import { CompactTable } from '@table-library/react-table-library/compact';
import { usePagination } from '@table-library/react-table-library/pagination';
import Icon, { IconNames } from 'components/Icon';
import colors from 'constants/colors';
import { buttonTypes, ITableColumn, sortFns } from 'constants/tables';
import { CSVLink } from 'react-csv';
import { inputDate } from 'helpers/date';
import { ColumnsToggle } from './ColumnsToggle';
import { showToast } from 'components/Toast';
import { ISortState } from 'store/viewPreferences/types';
import { DashboardScreen, trackEvent, trackTablePaginated } from 'helpers/mixpanelEvents';

const SEARCH_SUBMITTED_DEBOUNCE_MS = 800;

interface Props {
  columns: ITableColumn[];
  data: any;
  title?: string;
  subtitle?: string;
  pageSize?: number;
  withPagination?: boolean;
  withDownloadCSV?: boolean;
  withColumnFilter?: boolean;
  updateColumns?: (columns: ITableColumn[]) => void;
  onSaveColumnDefaults?: (columns: ITableColumn[]) => void;
  onSortChanged?: (sortState: ISortState) => void;
  onSearchChanged?: (searchText: string) => void;
  searchString?: string | null;
  sortState?: ISortState | null;
  onColumnVisibilityChanged?: (visibleColumns: string[]) => void;
  screen?: DashboardScreen;
}

export const CustomTable: FC<Props> = ({
  columns,
  data,
  title,
  subtitle,
  pageSize = 10,
  withPagination,
  withDownloadCSV,
  withColumnFilter,
  updateColumns,
  onSaveColumnDefaults,
  onSortChanged,
  onSearchChanged,
  searchString,
  sortState,
  screen,
}) => {
  // Filter visible columns first
  const visibleColumns = columns.filter(column => column.visible);

  // Create a unique key based on visible columns to force re-initialization
  const columnsKey = visibleColumns.map(col => col.accessor).join(',');

  // Create filtered data that only includes visible columns
  const filteredData = useMemo(() => {
    return data.map((rowData, index) => {
      const filteredRow: any = {
        id: rowData.id || `row-${index}`,
        ...rowData,
      };
      visibleColumns.forEach(column => {
        filteredRow[column.accessor] = rowData[column.accessor];
      });
      return filteredRow;
    });
  }, [data, visibleColumns]);

  //copy link
  const handleCopy = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => showToast('Link copied!', 'success'))
      .catch(() => showToast('Failed to copy link. Please copy it manually.', 'error'));
  };

  //search
  const [search, setSearch] = useState(searchString || '');
  // Sync local search state with searchString prop
  useEffect(() => {
    setSearch(searchString || '');
  }, [searchString]);

  // Debounced here, not in a useEffect on `search` — `searchString` gets restored from
  // sessionStorage on a later render, which an effect can't tell apart from real typing.
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const handleSearch = event => {
    const value = event.target.value;
    setSearch(value);
    onSearchChanged?.(value);

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!screen || !value) return;
    searchDebounceRef.current = setTimeout(() => {
      trackEvent('Table Search Submitted', { screen });
    }, SEARCH_SUBMITTED_DEBOUNCE_MS);
  };
  const tableData = {
    nodes: filteredData.filter(item => {
      if (!search) return true;
      const searchLower = search.toLowerCase();
      return visibleColumns.some(column => {
        const value = item[column.accessor];
        return value && value.toString().toLowerCase().includes(searchLower);
      });
    }),
  };

  const csvData = [
    [' ', ...visibleColumns.map(col => col.label)],
    ...filteredData.map((rowData, i) => [
      i,
      ...visibleColumns.map(col => col.accessor).map(columnName => rowData[columnName]),
    ]),
  ];

  //sort
  const sort = useSort(
    filteredData,
    {
      state: {
        sortKey: sortState?.sortKey,
        reverse: sortState?.reverse,
      },
      onChange: (action, state) => {
        if (action.type !== 'TOGGLE_SORT') return;
        if (screen) {
          trackEvent('Table Column Sorted', {
            screen,
            sort_key: state.sortKey,
            reverse: state.reverse,
          });
        }
        onSortChanged?.({
          sortKey: state.sortKey,
          reverse: state.reverse,
        });
      },
    },
    {
      sortIcon: {
        margin: '4px',
        iconDefault: <Icon name={IconNames.ChevronUpDown} size={12} />,
        iconUp: <Icon name={IconNames.ChevronUp} size={12} />,
        iconDown: <Icon name={IconNames.ChevronDown} size={12} />,
      },
      sortFns,
    }
  );

  //pagination - use columnsKey to force re-initialization when columns change
  const pagination = usePagination(tableData, { state: { page: 0, size: pageSize } });
  const { state: paginationState, fns: paginationFns } = pagination;
  const { page, size } = paginationState;
  const { onSetPage } = paginationFns;

  const totalPages = Math.ceil(tableData.nodes.length / size);
  const canPreviousPage = page > 0;
  const canNextPage = page < totalPages - 1;

  // Reset to first page when search changes so filtered results are shown from the start
  useEffect(() => {
    if (withPagination) {
      onSetPage(0);
    }
  }, [search]);

  const previousPage = () => {
    if (canPreviousPage) {
      if (screen) trackTablePaginated({ screen, direction: 'previous', page: page - 1 });
      onSetPage(page - 1);
    }
  };

  const nextPage = () => {
    if (canNextPage) {
      if (screen) trackTablePaginated({ screen, direction: 'next', page: page + 1 });
      onSetPage(page + 1);
    }
  };

  const processedColumns = visibleColumns.map(column => ({
    ...column,
    renderCell: column.renderCell
      ? (item: any) => column.renderCell?.(item, handleCopy)
      : undefined,
  }));

  const visibleColumnAccessors = (updatedColumns: ITableColumn[]) =>
    updatedColumns.filter(column => column.visible).map(column => column.accessor);

  const handleColumnsSave = (updatedColumns: ITableColumn[]) => {
    if (screen) {
      trackEvent('Table Columns Saved', {
        screen,
        visible_columns: visibleColumnAccessors(updatedColumns),
      });
    }
    updateColumns?.(updatedColumns);
  };

  const handleColumnsSaveAsDefault = (updatedColumns: ITableColumn[]) => {
    if (screen) {
      trackEvent('Table Columns Saved As Default', {
        screen,
        visible_columns: visibleColumnAccessors(updatedColumns),
      });
    }
    onSaveColumnDefaults?.(updatedColumns);
  };

  const handleCsvExport = () => {
    if (screen) trackEvent('Table Exported To CSV', { screen });
  };

  return (
    <div key={columnsKey}>
      <div className="CustomTableHeader">
        <div className="grid-column">
          <span className="oath-h4 inkBlue capitalize">{title}</span>
          <span className="oath-bodysmall gray">{subtitle}</span>
        </div>
        <div className="RightContainer">
          <div className="search-container">
            <input
              type="search"
              placeholder="Search"
              aria-label="Search donations"
              value={search}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          {withColumnFilter && (
            <div className="FilterOptions">
              {withDownloadCSV && (
                <CSVLink
                  className="csv-link"
                  filename={`Oath_Donations_${inputDate(new Date().getTime())}.csv`}
                  data={csvData}
                  onClick={handleCsvExport}
                >
                  Export to CSV
                </CSVLink>
              )}
              <ColumnsToggle
                onSave={handleColumnsSave}
                onSaveColumnDefaults={onSaveColumnDefaults ? handleColumnsSaveAsDefault : undefined}
                columns={columns}
              />
            </div>
          )}
        </div>
      </div>
      <div className="customTable">
        <CompactTable
          columns={processedColumns}
          data={tableData}
          sort={sort}
          pagination={pagination}
        />
      </div>
      {withPagination && (
        <div className="pagination-bottom">
          <div className="-pagination">
            <div className="col-lg-6">
              <div className="-previous">
                <button
                  type="button"
                  onClick={previousPage}
                  disabled={!canPreviousPage}
                  className="-btn"
                >
                  Previous
                </button>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="-next">
                <button type="button" onClick={nextPage} disabled={!canNextPage} className="-btn">
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const tableButton = (handlePress, type, id, disabledReason?: string) => {
  const isDisabled = Boolean(disabledReason);
  return (
    <div className="padding0 col-md-6">
      <div
        className={classNames('table-action-link', isDisabled ? 'gray' : 'inkBlue')}
        onClick={isDisabled ? undefined : () => handlePress(type, id)}
      >
        <Icon name={buttonTypes[type].icon} color={isDisabled ? colors.gray : colors.inkBlue} />
        <span>{type}</span>
      </div>
      {/* Rendered as text, not a title attribute: a tooltip on a disabled control never fires
          in Chrome or Safari, and is unreachable by touch and screen readers either way. */}
      {isDisabled && (
        <span className="oath-bodysmall gray table-action-reason">{disabledReason}</span>
      )}
    </div>
  );
};
